import { computed, Injectable, signal } from '@angular/core';
import {
  CREDENTIALS_KEY,
  getAccountId,
  getActorAccountId,
  ORIGIN_CREDENTIALS_KEY,
  readCredentials,
  readStoredCredentials,
} from './authentication';
import type { OAuthTokens } from './authentication.types';

/** The `(account, actor)` pair identifying WHO the active access token represents. */
interface IdentityTuple {
  readonly account: string | null;
  readonly actor: string | null;
}

function identityTupleOf(accessToken: string): IdentityTuple {
  return { account: getAccountId(accessToken), actor: getActorAccountId(accessToken) };
}

function sameIdentity(a: IdentityTuple, b: IdentityTuple): boolean {
  return a.account === b.account && a.actor === b.actor;
}

/**
 * Holds the credentials/accessToken/refreshToken signals and owns the
 * localStorage I/O for the authentication session. Deliberately has zero
 * Apollo dependency so it can be injected by the Apollo factory without
 * creating the apolloFactory ↔ AuthenticationService ↔ Apollo cycle.
 *
 * State writers (`signIn`, `signOut`, etc.) live on `AuthenticationService`
 * and delegate to this store. Read-only consumers (route guards, components
 * that gate UI on `authenticated`, the Apollo factory's `SetContextLink`)
 * inject this store directly.
 *
 * ## Credential stack (impersonation)
 *
 * `beginImpersonation`/`endImpersonation` add a SECOND slot (`originCredentials`) so an
 * operator's own session can be stashed behind an impersonated one and restored later.
 * `isImpersonating` deliberately derives from the ACTIVE access token's actor claim (`act.sub`),
 * never from the presence of the stash — the stash is UX state that can go stale (another tab, a
 * hand-edited localStorage entry); the token is the only authority.
 */
@Injectable()
export class TokenStore {
  private readonly _credentials = signal<OAuthTokens | null>(readCredentials());
  private readonly _originCredentials = signal<OAuthTokens | null>(
    readStoredCredentials(ORIGIN_CREDENTIALS_KEY),
  );
  private readonly _identityGeneration = signal(0);
  /**
   * The `(account, actor)` tuple as of the LAST synced write — never persisted (constraint: the
   * generation is in-memory session state, so a reload legitimately starts a fresh sequence). Seeded
   * from whatever `_credentials` hydrated from localStorage at construction, so the FIRST write after
   * a reload is compared against the identity that was actually already active, not against empty.
   */
  private _lastIdentityTuple: IdentityTuple = identityTupleOf(
    this._credentials()?.accessToken ?? '',
  );

  readonly authenticated = computed(() => this._credentials() !== null);
  readonly accessToken = computed(() => this._credentials()?.accessToken ?? '');
  readonly refreshToken = computed(() => this._credentials()?.refreshToken ?? '');
  readonly isImpersonating = computed(() => getActorAccountId(this.accessToken()) !== null);
  /**
   * `true` iff the origin slot holds a session to roll back to. Read it BEFORE deciding to unwind
   * an impersonation: `isImpersonating()` says "the ACTIVE token is impersonated", which stays
   * `true` after a no-op `endImpersonation()`, so a caller that unwinds blindly with an empty
   * stash dead-ends on the same expired token. It is UX state, never an authorization signal —
   * `isImpersonating` must keep deriving from the token, not from this.
   */
  readonly hasStashedSession = computed(() => this._originCredentials() !== null);
  /**
   * Monotonic counter over the `(account, actor)` identity tuple derived from the ACTIVE access
   * token — NOT over which method was called. Increments exactly when a write leaves the store with
   * a DIFFERENT tuple than the one before it; a refresh rotation that keeps the same account and
   * actor claim leaves it untouched, so a retry after a transparent refresh stays legal. In-memory
   * only — never persisted, so a reload legitimately starts a fresh sequence.
   */
  readonly identityGeneration = this._identityGeneration.asReadonly();

  setCredentials(credentials: OAuthTokens): void {
    localStorage.setItem(CREDENTIALS_KEY, btoa(JSON.stringify(credentials)));
    this._credentials.set(credentials);
    this.syncIdentityGeneration(credentials.accessToken);
  }

  getCredentials(): OAuthTokens | null {
    return this._credentials();
  }

  /**
   * Re-reads the ACTIVE credentials from localStorage into the signal. The store hydrates only at
   * construction, so a sibling tab's rotation is invisible to this tab until something calls this.
   * Touches neither the origin slot nor the storage itself, so an impersonation stash is never
   * disturbed. Returns what storage holds (`null` when the session was cleared elsewhere).
   */
  syncFromStorage(): OAuthTokens | null {
    const stored = readCredentials();
    this._credentials.set(stored);
    this.syncIdentityGeneration(stored?.accessToken ?? '');
    return stored;
  }

  clear(): void {
    localStorage.removeItem(CREDENTIALS_KEY);
    localStorage.removeItem(ORIGIN_CREDENTIALS_KEY);
    this._credentials.set(null);
    this._originCredentials.set(null);
    this.syncIdentityGeneration('');
  }

  /**
   * Recomputes the `(account, actor)` tuple for `accessToken` and advances `identityGeneration`
   * only if it differs from the tuple recorded at the previous write. Called from every
   * state-mutating write (`setCredentials` and `clear`; `beginImpersonation`/`endImpersonation` go
   * through `setCredentials`), so no call site can forget to advance it — the tuple decides, not
   * the caller.
   */
  private syncIdentityGeneration(accessToken: string): void {
    const tuple = identityTupleOf(accessToken);
    if (!sameIdentity(tuple, this._lastIdentityTuple)) {
      this._identityGeneration.update((generation) => generation + 1);
    }
    this._lastIdentityTuple = tuple;
  }

  /**
   * Stashes the currently active credentials into the origin slot, then installs `credentials`
   * as the active session. Refuses (no-op) when the origin slot is already occupied, so a nested
   * call can never overwrite and lose the operator's real session (mirrors backend veto V2).
   *
   * Returns `true` when the swap happened, `false` when it was refused. Security finding F7: the
   * caller MUST check this — a refused swap leaves the ACTIVE credentials as the operator's own
   * (unswitched) token, so blindly proceeding as if the swap succeeded (re-materializing session
   * state, navigating away) would make the operator believe they act as the target while every
   * request still carries their own privileges.
   */
  beginImpersonation(credentials: OAuthTokens): boolean {
    if (this._originCredentials() !== null) return false;

    const current = this._credentials();
    if (current) {
      localStorage.setItem(ORIGIN_CREDENTIALS_KEY, btoa(JSON.stringify(current)));
      this._originCredentials.set(current);
    }

    this.setCredentials(credentials);
    return true;
  }

  /**
   * Restores the stashed origin credentials as the active session and empties the origin slot.
   * A no-op when no stash exists.
   */
  endImpersonation(): void {
    const origin = this._originCredentials();
    if (!origin) return;

    localStorage.removeItem(ORIGIN_CREDENTIALS_KEY);
    this._originCredentials.set(null);
    this.setCredentials(origin);
  }
}
